import { useState } from "react";
import { useLocation } from "wouter";
import {
  getListInventoryPlansQueryKey,
  useDeleteInventoryPlan,
  useListInventoryPlans,
  useListSpaces,
  useUpdateInventoryPlan,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { IndianRupee, Pencil, Plus, Trash2 } from "lucide-react";
import { ArchivePlanDialog } from "@/components/spaces/archive-plan-dialog";
import { ChangePriceDialog } from "@/components/plan-editor/change-price-dialog";
import { useToast } from "@/hooks/use-toast";
export default function BillingPlans() {
  const [, navigate] = useLocation();
  const [statusFilter, setStatusFilter] = useState<"Active" | "Archived" | "all">("Active");
  const [resourceFilter, setResourceFilter] = useState<string>("all");

  const { data: plans = [], isLoading } = useListInventoryPlans();
  const { data: spaces = [] } = useListSpaces();
  const spaceById = Object.fromEntries(spaces.map((s) => [s.id, s]));
  const qc = useQueryClient();
  const { toast } = useToast();
  const updatePlan = useUpdateInventoryPlan();
  const deletePlan = useDeleteInventoryPlan();
  const [archiveTarget, setArchiveTarget] = useState<{ id: number; name: string } | null>(null);
  const [pricingTarget, setPricingTarget] = useState<
    { id: number; planName: string; monthlyPrice: number | string } | null
  >(null);

  async function tryDelete(plan: { id: number; planName: string }) {
    try {
      await deletePlan.mutateAsync({ id: plan.id });
      qc.invalidateQueries({ queryKey: getListInventoryPlansQueryKey() });
      toast({ title: "Plan deleted", description: plan.planName.replace(/_/g, " ") });
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number; data?: { error?: string } } })?.response?.status;
      const serverMsg = (err as { response?: { status?: number; data?: { error?: string } } })?.response?.data?.error;
      if (status === 409) {
        // FK conflict — has memberships or other references. Fall back to archive flow.
        toast({
          title: "Can't hard-delete — archive instead",
          description: serverMsg ?? "This plan has live references. Archiving keeps existing memberships intact.",
        });
        setArchiveTarget({ id: plan.id, name: plan.planName });
        return;
      }
      toast({
        title: "Couldn't delete plan",
        description: (err as Error).message ?? "Try again.",
        variant: "destructive",
      });
    }
  }

  async function flipStatus(plan: { id: number; planName: string; status: string }, nextStatus: "Active" | "Archived") {
    try {
      await updatePlan.mutateAsync({ id: plan.id, data: { status: nextStatus } });
      qc.invalidateQueries({ queryKey: getListInventoryPlansQueryKey() });
      toast({
        title: nextStatus === "Active" ? "Plan reactivated" : "Plan archived",
        description: plan.planName.replace(/_/g, " "),
      });
    } catch (err: unknown) {
      toast({
        title: "Couldn't update plan",
        description: (err as Error).message ?? "Try again.",
        variant: "destructive",
      });
    }
  }

  const filtered = plans.filter((p) => {
    if (statusFilter !== "all" && p.status !== statusFilter) return false;
    if (resourceFilter !== "all" && (p.resourceType ?? "") !== resourceFilter) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Plans</h1>
          <p className="text-sm text-muted-foreground">
            All plans across spaces. Click a row or <span className="font-medium">Edit</span> to open the plan editor.
          </p>
        </div>
        <Button onClick={() => navigate("/billing/plans/new")} size="sm">
          <Plus className="mr-1 h-4 w-4" />
          New Plan
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as typeof statusFilter)}>
          <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Active">Active only</SelectItem>
            <SelectItem value="Archived">Archived only</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
        <Select value={resourceFilter} onValueChange={setResourceFilter}>
          <SelectTrigger className="w-[200px]"><SelectValue placeholder="All resource types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All resource types</SelectItem>
            <SelectItem value="Workstation">Workstation</SelectItem>
            <SelectItem value="Private_Cabin">Private Cabin</SelectItem>
            <SelectItem value="Meeting_Room">Meeting Room</SelectItem>
            <SelectItem value="Conference_Room">Conference Room</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Plan</TableHead>
              <TableHead>Space</TableHead>
              <TableHead>Cadence</TableHead>
              <TableHead>Resource</TableHead>
              <TableHead className="text-right">Price</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              [...Array(4)].map((_, i) => (
                <TableRow key={i}>
                  {[...Array(7)].map((__, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-20" /></TableCell>
                  ))}
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="h-24 text-center text-sm text-muted-foreground">
                  No plans match these filters.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((p) => (
                <TableRow
                  key={p.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/billing/plans/${p.id}`)}
                >
                  <TableCell className="font-medium">{p.planName.replace(/_/g, " ")}</TableCell>
                  <TableCell className="text-sm">{spaceById[p.spaceId]?.name ?? `Space #${p.spaceId}`}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {p.cadence === "Recurring" ? "Recurring" : "Non-Recurring"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {p.resourceType ? p.resourceType.replace(/_/g, " ") : "—"}
                  </TableCell>
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() =>
                        setPricingTarget({ id: p.id, planName: p.planName, monthlyPrice: p.monthlyPrice ?? 0 })
                      }
                      title="Change price"
                      className="font-mono text-xs underline-offset-2 decoration-dotted hover:underline"
                    >
                      {p.monthlyPrice != null ? `₹${Number(p.monthlyPrice).toLocaleString("en-IN")}` : "—"}
                    </button>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={p.status === "Active"}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            flipStatus(p, "Active");
                          } else {
                            setArchiveTarget({ id: p.id, name: p.planName });
                          }
                        }}
                        disabled={updatePlan.isPending}
                      />
                      <Badge variant={p.status === "Active" ? "default" : "secondary"} className="text-xs">
                        {p.status}
                      </Badge>
                    </div>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setPricingTarget({ id: p.id, planName: p.planName, monthlyPrice: p.monthlyPrice ?? 0 })
                        }
                        title="Change price"
                      >
                        <IndianRupee className="mr-1 h-3.5 w-3.5" />
                        Change Price
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(`/billing/plans/${p.id}`)}
                        title="Edit plan"
                      >
                        <Pencil className="mr-1 h-3.5 w-3.5" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => tryDelete({ id: p.id, planName: p.planName })}
                        title="Delete plan (falls back to archive if it has references)"
                        disabled={deletePlan.isPending || updatePlan.isPending}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pricingTarget && (
        <ChangePriceDialog
          open={pricingTarget !== null}
          onOpenChange={(open) => { if (!open) setPricingTarget(null); }}
          plan={pricingTarget}
        />
      )}

      <ArchivePlanDialog
        open={archiveTarget !== null}
        onOpenChange={(open) => { if (!open) setArchiveTarget(null); }}
        planName={archiveTarget?.name ?? ""}
        confirming={updatePlan.isPending}
        onConfirm={async () => {
          if (!archiveTarget) return;
          await flipStatus(
            { id: archiveTarget.id, planName: archiveTarget.name, status: "Active" },
            "Archived",
          );
          setArchiveTarget(null);
        }}
      />
    </div>
  );
}
